/**
 * Investors.js — the investor waterfall of one deal
 * =================================================
 * Brian, 29 October 2026: "on each deal in deal reports, please also add the
 * investor waterfall with the full breakdown of cash deployed, cash returned,
 * cash distributed, etc. Everything we would want to see as a money manager in
 * this regard."
 *
 * The Summary tab says what the fund did with the deal. This tab says what
 * happened to the investors' side of it. Every investor lent Helena Partners
 * Inc under a loan note for this deal, so their capital, the profit the
 * waterfall accrued to them, and everything distributed or recycled back out
 * are movements on those notes; the server reads them off the ledger
 * (reports/services/deal_waterfall.py) and nothing is added up here except the
 * running total of the history, which the server's own balances check.
 *
 * In the order a reader asks: how much went in and what came back (the
 * figures at the top); how the house terms split it, booked and at today's
 * marks (the waterfall); who is owed what — each investor, the deal team and
 * Helena Partners Inc on one table, with what is due to each now (Brian, 29
 * October 2026: "I want to see money due to each invesot in that table and
 * then deal team and hpi all on one table"), an investor's row opening to its
 * breakdown and its own dated history; where the deal's proceeds went; and
 * every movement on the notes, one row per date, with the investors across
 * the top the way the Deals P&L lays a deal out.
 *
 * Data: GET /api/v1/reports/deal-investor-waterfall/?deal=<id>
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../api/client";
import { Card, EmptyState, LoadingSpinner } from "../../components/ui";
import { ExportCsvButton, InfoTip } from "../../components/PageTools";
import { multiple, pct, toNumber, DASH } from "../../components/format";
import { dealReportPath } from "../../components/links";
import theme from "../../components/theme";
import { day, dealMoney, exactMoney } from "./figures";
import { Tile } from "./Tiles";

export const WATERFALL_URL = "/reports/deal-investor-waterfall/";

// Read once per deal per visit, like the Activity tab; the page's Refresh
// clears it.
const waterfallCache = new Map();

export function clearWaterfallCache() {
  waterfallCache.clear();
}

function useWaterfall(dealId) {
  const [state, setState] = useState(() => ({ data: waterfallCache.get(dealId) || null, error: "" }));
  useEffect(() => {
    let live = true;
    const cached = waterfallCache.get(dealId);
    setState({ data: cached || null, error: "" });
    if (cached) return undefined;
    api
      .get(WATERFALL_URL, { params: { deal: dealId } })
      .then((res) => {
        waterfallCache.set(dealId, res.data);
        if (live) setState({ data: res.data, error: "" });
      })
      .catch((err) => live && setState({ data: null, error: err.response?.data?.error || err.message || "Failed to load" }));
    return () => {
      live = false;
    };
  }, [dealId]);
  return state;
}

const usd = (value) => dealMoney(value, "USD");
const cents = (value) => exactMoney(value, "USD");

/** "1.42x", or a dash where nothing went in. */
export const multipleText = (value) => (toNumber(value) === null ? DASH : multiple(value));

/** A share of a whole, "80.0%", from a fraction. */
export const shareText = (fraction) => (toNumber(fraction) === null ? DASH : pct(Number(fraction) * 100, 1));

/** Green above 1.00x, red below, nothing at a multiple that prints as 1.00x. */
export function multipleTone(value) {
  const n = toNumber(value);
  if (n === null || Math.abs(n - 1) < 0.005) return undefined;
  return n > 1 ? "positive" : "negative";
}

const toneColor = (value) => {
  const n = toNumber(value);
  if (!n) return theme.text;
  return n > 0 ? theme.success : theme.danger;
};

/** A deal on the other side of a recycling: the company and the instrument. */
export function otherDealText(other) {
  if (!other) return "";
  return [other.company, other.instrument].filter(Boolean).join(" · ");
}

// A movement moves the note one way or the other: capital and profit onto it,
// a distribution, a recycling out and a reversed accrual off it. The history
// shows each with that sign, so its running total is what the notes stand at.
const SIGN = { in: 1, profit: 1, other: 1, out: -1, loss: -1 };

export const historySign = (row) => SIGN[row.direction] ?? 1;

/** What a history row is, in words, with the deal it went to or came from. */
export function historyLabel(row) {
  const other = otherDealText(row.other_deal);
  // A declaration taken back onto the note (a negative distribution) is
  // stored as a negative declaration.
  if (row.kind === "declared" && Number(row.total) < 0) return "Distribution taken back onto the note";
  if (row.kind === "recycled_in") return other ? `Recycled in from ${other}` : row.label;
  if (row.kind === "recycled_out") return other ? `Recycled into ${other}` : row.label;
  return row.label;
}

/** The history with each row's signed total and the notes' running total. */
export function historyRows(history) {
  let running = 0;
  return (history || []).map((row) => {
    const sign = historySign(row);
    const total = sign * Number(row.total);
    running += total;
    return { ...row, sign, signedTotal: total, running };
  });
}

export function historyCsvColumns(order) {
  return [
    { label: "Date", value: (r) => r.date },
    { label: "What", value: (r) => historyLabel(r) },
    ...(order || []).map((name) => ({
      label: name,
      value: (r) => (r.amounts[name] === undefined ? null : r.sign * Number(r.amounts[name])),
    })),
    { label: "Total", value: (r) => r.signedTotal },
    { label: "Notes stand at", value: (r) => r.running },
  ];
}

/** "$335,767 more than booked", or less: the marks can now support less. */
export function toComeText(value) {
  const n = toNumber(value);
  if (n === null) return "";
  if (Math.abs(n) < 0.5) return "what is booked";
  return `${usd(Math.abs(n))} ${n > 0 ? "more" : "less"} than booked`;
}

const numCell = { textAlign: "right", whiteSpace: "nowrap" };
const muted = { color: theme.textMuted };

/* ── The figures at the top ──────────────────────────────────────────── */

function Headline({ data }) {
  const t = data.lenders_total;
  const marks = data.waterfall.at_marks;
  const n = data.investors.length;
  const capitalBits = [
    `lent in cash ${usd(t.capital_lent)}`,
    Number(t.capital_recycled_in) ? `recycled in ${usd(t.capital_recycled_in)}` : null,
    Number(t.capital_reinvested) ? `lent back in ${usd(t.capital_reinvested)}` : null,
  ].filter(Boolean);
  const owedBack = Number(t.owed_back) ? ` less ${usd(t.owed_back)} they owe back` : "";
  return (
    <Card title="Investors in this deal" subtitle="From their loan notes on the general ledger, in USD">
      <div className="dr-tiles">
        <Tile
          label="Investors' capital"
          value={usd(t.capital)}
          note={`${n} ${n === 1 ? "investor" : "investors"} · ${capitalBits.join(" · ")}`}
        />
        <Tile
          label="Back to investors"
          value={usd(t.returned)}
          tone={Number(t.returned) > 0 ? "positive" : undefined}
          note={`distributed ${usd(t.distributed)} · recycled into other deals ${usd(t.recycled_out)}`}
        />
        <Tile
          label="Still owed on their notes"
          value={usd(t.note_balance)}
          note="capital not yet back and profit booked, not yet paid out"
          info="A note's balance is everything the investor is still owed on this deal: on a deal still running, the capital not yet returned and any profit the waterfall has booked and not yet distributed. Below nil, more has come off the note than was booked onto it."
        />
        <Tile
          label="Investors' multiple"
          value={multipleText(t.multiple_booked)}
          tone={multipleTone(t.multiple_booked)}
          note={`(back + still owed${owedBack}) ÷ capital, as booked`}
        />
        {marks ? (
          <Tile
            label="At today's marks"
            value={multipleText(marks.lenders_multiple)}
            tone={multipleTone(marks.lenders_multiple)}
            note={`${usd(marks.lenders_value)} to investors if what is still held sold at its marks · ${toComeText(marks.lenders_to_come)}`}
          />
        ) : (
          <Tile
            label="Cash back per dollar lent"
            value={multipleText(t.dpi)}
            note="distributed and recycled ÷ capital, before anything still owed"
          />
        )}
      </div>
    </Card>
  );
}

/* ── No notes on this record ─────────────────────────────────────────── */

function NoInvestors({ data }) {
  const siblings = data.siblings_with_notes || [];
  return (
    <Card title="Investors in this deal">
      <div style={{ fontSize: 13, color: theme.textSecondary, lineHeight: 1.55 }}>
        <p style={{ margin: "0 0 8px" }}>No investor holds a loan note on this deal record.</p>
        {siblings.length > 0 ? (
          <>
            <p style={{ margin: "0 0 6px" }}>
              The investors in {siblings[0].company} hold their notes on{" "}
              {siblings.length === 1 ? "another of its records" : `${siblings.length} of its other records`}, and what
              this one makes is shared over them:
            </p>
            <ul style={{ margin: "0 0 0 18px", padding: 0 }}>
              {siblings.map((s) => (
                <li key={s.id}>
                  <Link className="dr-link" to={`${dealReportPath(s.id)}?tab=investors`}>
                    {otherDealText(s)}
                  </Link>
                  {s.funded_on ? `, funded ${day(s.funded_on)}` : ""}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <p style={{ margin: 0 }}>
            No other record of the same company holds any either, so what this deal owes is held by Helena Partners Inc
            and its investors in shares the book does not record.
          </p>
        )}
      </div>
    </Card>
  );
}

/* ── The waterfall ───────────────────────────────────────────────────── */

export function waterfallRows(data) {
  const booked = data.waterfall.booked;
  const marks = data.waterfall.at_marks;
  const rows = [
    {
      key: "capital",
      label: "Capital, returned first",
      note: "the investors' capital in this deal, pro rata to which the deal pays them back first",
      lenders: data.lenders_total.capital,
      deal_team: null,
      hpi: null,
      total: data.lenders_total.capital,
    },
    {
      key: "booked",
      label: "Profit booked to date",
      note: "what the waterfall journals have accrued to each",
      lenders: booked.lenders,
      deal_team: booked.deal_team,
      hpi: booked.hpi,
      total: booked.total,
      shares: booked.shares,
    },
  ];
  if (marks) {
    rows.push(
      {
        key: "marks",
        label: "Profit at today's marks",
        note: `the deal's P&L if what is still held (${usd(marks.still_held)}) sold at its marks, split on the terms`,
        lenders: marks.lenders,
        deal_team: marks.deal_team,
        hpi: marks.hpi,
        total: marks.profit,
      },
      {
        key: "to_book",
        label: "Still to book at those marks",
        note: "the marks' split less what is booked; below nil where more is booked than the marks now support",
        lenders: marks.to_book.lenders,
        deal_team: marks.to_book.deal_team,
        hpi: marks.to_book.hpi,
        total: Number(marks.to_book.lenders) + Number(marks.to_book.deal_team) + Number(marks.to_book.hpi),
      }
    );
  }
  return rows;
}

function WaterfallCard({ data }) {
  const rows = waterfallRows(data);
  const terms = data.terms;
  const cash = data.cash;
  const lendersPct = shareText(terms.lenders_pct);
  const fromProceeds = toNumber(cash.reinvested_usd);
  return (
    <Card
      title="The waterfall"
      subtitle={terms.no_promote ? "A PPLN deal: capital back first, then all of the profit to the investors" : "Capital back first, then the profit 80 / 15 / 5"}
      action={
        <InfoTip align="right" wide>
          <p>The terms: {terms.reads_as}.</p>
          <p>
            Booked is what the ledger carries: the profit each waterfall journal credited to the investors' notes, to
            the deal team's payable and to Helena Partners Inc's own carry. The deal lead takes no promote on his own
            money and deal-team members take none, so on many deals the investors' share of what was booked is above{" "}
            {lendersPct}.
          </p>
          <p>
            At today's marks is a projection, not a booking: the deal report's own P&L, with what is still held at its
            marks — at cost wherever nothing marks it — split on the terms, and the investors' part shared by capital.
          </p>
        </InfoTip>
      }
      noPad
    >
      <div className="dr-table-scroll">
        <table className="dr-table dr-wf-table">
          <thead>
            <tr>
              <th>Tier</th>
              <th className="num">Investors ({lendersPct})</th>
              <th className="num">Deal team ({shareText(terms.deal_team_pct)})</th>
              <th className="num">Helena Partners Inc ({shareText(terms.hpi_pct)})</th>
              <th className="num">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td>
                  <div style={{ fontWeight: 600 }}>{r.label}</div>
                  <div className="dr-wf-note">{r.note}</div>
                </td>
                {["lenders", "deal_team", "hpi"].map((tier) => (
                  <td key={tier} className="num">
                    {r[tier] === null ? <span style={muted}>{DASH}</span> : usd(r[tier])}
                    {r.shares && Number(r.total) > 0 && <div className="dr-wf-note">{shareText(r.shares[tier])}</div>}
                  </td>
                ))}
                <td className="num" style={{ fontWeight: 600 }}>
                  {usd(r.total)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="dr-wf-foot">
        {!data.waterfall.at_marks && data.waterfall.at_marks_withheld_because && (
          <div>
            At today's marks: not shown — {data.waterfall.at_marks_withheld_because}.
            {(data.company_records || []).length > 0 && (
              <>
                {" "}The other {(data.company_records || []).length === 1 ? "record" : "records"}:{" "}
                {data.company_records.map((d, i) => (
                  <span key={d.id}>
                    {i ? ", " : ""}
                    <Link className="dr-link" to={`${dealReportPath(d.id)}?tab=investors`}>
                      {d.instrument || d.name}
                    </Link>
                  </span>
                ))}
                .
              </>
            )}
          </div>
        )}
        {toNumber(cash.capital_deployed) !== null && (
          <div>
            The fund put {dealMoney(cash.capital_deployed, cash.stats_currency || "USD")} of capital into the deal against{" "}
            {usd(data.lenders_total.capital)} of investors' capital
            {fromProceeds ? `; ${usd(fromProceeds)} of what it spent was paid out of the deal's own proceeds` : ""}.
          </div>
        )}
      </div>
    </Card>
  );
}

/* ── Each investor ───────────────────────────────────────────────────── */

function Pair({ label, value, strong }) {
  return (
    <div className="dr-inv-pair">
      <span>{label}</span>
      <span style={{ fontWeight: strong ? 600 : 400, color: theme.text }}>{value}</span>
    </div>
  );
}

function InvestorDetail({ row, history }) {
  const mine = history.filter((h) => h.amounts[row.investor] !== undefined);
  return (
    <div className="dr-inv-detail">
      <div className="dr-inv-groups">
        <div>
          <div className="dr-inv-head">Capital in</div>
          <Pair label="Lent in cash" value={cents(row.capital_lent)} />
          <Pair label="Recycled in from other deals" value={cents(row.capital_recycled_in)} />
          <Pair label="A distribution or promote lent back in" value={cents(row.capital_reinvested)} />
          <Pair label="Capital in" value={cents(row.capital)} strong />
          {row.first_in && <Pair label="First in" value={day(row.first_in)} />}
        </div>
        <div>
          <div className="dr-inv-head">Profit and what came back</div>
          <Pair label="Profit accrued by the waterfall" value={cents(row.profit_accrued)} />
          {Number(row.loss_charged) !== 0 && <Pair label="Profit reversed or loss charged" value={cents(-Number(row.loss_charged))} />}
          <Pair label="Declared to a payable" value={cents(row.declared)} />
          {Number(row.paid_off_note) !== 0 && <Pair label="Paid straight off the note" value={cents(row.paid_off_note)} />}
          <Pair label="Recycled into other deals" value={cents(row.recycled_out)} />
          <Pair label="Back to the investor" value={cents(row.returned)} strong />
          {row.last_out && <Pair label="Last back" value={day(row.last_out)} />}
        </div>
        <div>
          <div className="dr-inv-head">Where they stand</div>
          <Pair label="Still owed on the note" value={cents(row.note_balance)} strong />
          {Number(row.owed_back) !== 0 && <Pair label="Owes back on this deal" value={cents(row.owed_back)} />}
          <Pair label="Declared on this deal, not yet paid" value={cents(row.payable_balance)} />
          <Pair label="Cash owed now, out of proceeds received" value={row.cash_owed_now === null ? "n/a" : cents(row.cash_owed_now)} />
          <Pair label="Value booked ÷ capital" value={`${cents(row.value_booked)} · ${multipleText(row.multiple_booked)}`} />
          {row.value_at_marks !== null && (
            <Pair label="At today's marks" value={`${cents(row.value_at_marks)} · ${multipleText(row.multiple_at_marks)}`} />
          )}
        </div>
      </div>
      {(row.reads_as || []).length > 0 && (
        <ul className="dr-inv-notes">
          {row.reads_as.map((text) => (
            <li key={text}>{text}</li>
          ))}
        </ul>
      )}
      {mine.length > 0 && (
        <table className="dr-inv-history">
          <tbody>
            {mine.map((h) => (
              <tr key={`${h.date}-${h.kind}-${h.other_deal ? h.other_deal.id : ""}`}>
                <td style={{ whiteSpace: "nowrap", ...muted }}>{day(h.date)}</td>
                <td>{historyLabel(h)}</td>
                <td style={numCell}>{cents(h.sign * Number(h.amounts[row.investor]))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * The rows beside the investors, and the line under everybody, in the table's
 * own columns. A figure that does not apply to a row (a deal-team member has
 * no capital in, Helena Partners Inc's carry is kept and paid to nobody) is
 * null and prints as a dash.
 */
export function payeeRows(data) {
  return (data.payees || []).map((p) => ({
    key: `${p.role}:${p.who}`,
    who: p.who,
    note: p.reads_as,
    role: p.role,
    capital: null,
    profit_booked: p.profit_booked,
    distributed: p.distributed,
    recycled_out: p.recycled_out,
    balance_owed: p.balance_owed,
    due_now: p.due_now,
    multiple: null,
    to_come_at_marks: p.to_come_at_marks,
  }));
}

/** Every row of the table, investors first, as the CSV writes them. */
export function owedTableRows(data) {
  const t = data.lenders_total;
  const investors = data.investors.map((r) => ({
    who: r.investor, role: "investor", capital: r.capital, profit_booked: r.profit_booked,
    distributed: r.distributed, recycled_out: r.recycled_out, balance_owed: r.note_balance,
    due_now: r.cash_owed_now, multiple: r.multiple_booked, to_come_at_marks: r.to_come_at_marks,
  }));
  const subtotal = data.investors.length
    ? [{ who: "All investors", role: "subtotal", capital: t.capital, profit_booked: t.profit_booked,
         distributed: t.distributed, recycled_out: t.recycled_out, balance_owed: t.note_balance,
         due_now: t.cash_owed_now, multiple: t.multiple_booked,
         to_come_at_marks: data.waterfall.at_marks ? data.waterfall.at_marks.lenders_to_come : null }]
    : [];
  const e = data.everyone || {};
  return [
    ...investors,
    ...subtotal,
    ...payeeRows(data),
    { who: "Everyone", role: "total", capital: t.capital, profit_booked: e.profit_booked,
      distributed: e.distributed, recycled_out: e.recycled_out, balance_owed: e.balance_owed,
      due_now: e.due_now, multiple: null, to_come_at_marks: e.to_come_at_marks },
  ];
}

const ROLE_LABEL = {
  investor: "Investor",
  subtotal: "All investors",
  deal_team: "Deal team promote",
  deal_team_unsplit: "Deal team promote",
  hpi: "Helena Partners Inc carry",
  unsplit: "Not split",
  unassigned: "Not booked to anyone",
  other: "Other",
  total: "Everyone",
};

export const OWED_CSV_COLUMNS = [
  { label: "Who", value: (r) => r.who },
  { label: "As", value: (r) => ROLE_LABEL[r.role] || r.role },
  { label: "Capital in", value: (r) => toNumber(r.capital) },
  { label: "Profit booked", value: (r) => toNumber(r.profit_booked) },
  { label: "Distributed", value: (r) => toNumber(r.distributed) },
  { label: "Recycled out", value: (r) => toNumber(r.recycled_out) },
  { label: "Balance owed", value: (r) => toNumber(r.balance_owed) },
  { label: "Due now", value: (r) => toNumber(r.due_now) },
  { label: "Multiple", value: (r) => toNumber(r.multiple) },
  { label: "To come at today's marks", value: (r) => toNumber(r.to_come_at_marks) },
];

const figure = (value) => (value === null || value === undefined ? <span style={muted}>{DASH}</span> : usd(value));
const dueFigure = (value) => (value === null || value === undefined ? "n/a" : usd(value));

function FigureCells({ row, marks }) {
  return (
    <>
      <td className="num">{row.capital === null ? <span style={muted}>{DASH}</span> : usd(row.capital)}</td>
      <td className="num" style={{ color: toneColor(row.profit_booked) }}>{figure(row.profit_booked)}</td>
      <td className="num">{figure(row.distributed)}</td>
      <td className="num">{figure(row.recycled_out)}</td>
      <td className="num">{figure(row.balance_owed)}</td>
      <td className="num dr-due">{dueFigure(row.due_now)}</td>
      <td className="num">{row.multiple === null ? <span style={muted}>{DASH}</span> : multipleText(row.multiple)}</td>
      {marks && <td className="num">{figure(row.to_come_at_marks)}</td>}
    </>
  );
}

function OwedCard({ data, history }) {
  const [open, setOpen] = useState({});
  const marks = data.waterfall.at_marks;
  const cols = 8 + (marks ? 1 : 0);
  const rows = owedTableRows(data);
  const byRole = (role) => rows.filter((r) => r.role === role);
  const investors = data.investors;
  const others = rows.filter((r) => !["investor", "subtotal", "total"].includes(r.role));
  return (
    <Card
      title="Who is owed what"
      subtitle="Each investor, the deal team and Helena Partners Inc. Open an investor for where each figure came from"
      action={<ExportCsvButton filename="deal-who-is-owed-what" columns={OWED_CSV_COLUMNS} rows={rows} />}
      noPad
    >
      <div className="dr-table-scroll">
        <table className="dr-table dr-inv-table">
          <thead>
            <tr>
              <th>Who</th>
              <th className="num">Capital in</th>
              <th className="num">Profit booked</th>
              <th className="num">Distributed</th>
              <th className="num">Recycled out</th>
              <th className="num">Balance owed</th>
              <th className="num dr-due">Due now</th>
              <th className="num">Multiple</th>
              {marks && <th className="num">To come at marks</th>}
            </tr>
          </thead>
          <tbody>
            {investors.map((r, i) => {
              const isOpen = Boolean(open[r.investor]);
              return (
                <React.Fragment key={r.investor}>
                  <tr className={`dr-inv-row${isOpen ? " is-open" : ""}`}>
                    <td>
                      <button
                        type="button"
                        className="dr-inv-toggle"
                        aria-expanded={isOpen}
                        onClick={() => setOpen((o) => ({ ...o, [r.investor]: !isOpen }))}
                      >
                        <span aria-hidden="true">{isOpen ? "▾" : "▸"}</span> {r.investor}
                      </button>
                      {(r.reads_as || []).length > 0 && !isOpen && <span className="dr-inv-flag" title={r.reads_as.join(" ")}> ●</span>}
                      <div className="dr-wf-note">investor · {shareText(r.capital_share)} of the capital</div>
                    </td>
                    <FigureCells row={rows[i]} marks={marks} />
                  </tr>
                  {isOpen && (
                    <tr className="dr-inv-open">
                      <td colSpan={cols}>
                        <InvestorDetail row={r} history={history} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {byRole("subtotal").map((r) => (
              <tr key="subtotal" className="dr-inv-subtotal">
                <td>All investors</td>
                <FigureCells row={r} marks={marks} />
              </tr>
            ))}
            {others.map((r) => (
              <tr key={`${r.role}:${r.who}`} className="dr-inv-payee">
                <td>
                  {r.who}
                  <div className="dr-wf-note">{r.note}</div>
                </td>
                <FigureCells row={r} marks={marks} />
              </tr>
            ))}
            {byRole("total").map((r) => (
              <tr key="total" className="dr-inv-total">
                <td>Everyone</td>
                <FigureCells row={r} marks={marks} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="dr-wf-foot">
        Due now is each one's share of the proceeds this deal has received and not yet paid out: capital back to the
        investors first, then the profit on the terms. Balance owed is what the book still owes them — an investor's note,
        a deal-team member's payable — which can be above or below what is due now. Distributed is what was declared or
        paid to them; recycled out is money moved onto another deal (for the deal team, promote lent back in), which never
        passes through a bank.
      </div>
    </Card>
  );
}

/* ── Where the deal's proceeds went ──────────────────────────────────── */

function StatementLine({ sign, label, value, strong, indent, note }) {
  return (
    <div className={`dr-wf-line${strong ? " is-strong" : ""}`} style={indent ? { paddingLeft: 22 } : undefined}>
      <span className="dr-wf-sign">{sign}</span>
      <span>
        {label}
        {note && <span style={muted}> — {note}</span>}
      </span>
      <span className="dr-wf-amount">{value}</span>
    </div>
  );
}

function ProceedsCard({ data }) {
  const cash = data.cash;
  const unavailable = cash.unavailable_because;
  return (
    <Card
      title="Where the deal's proceeds went"
      subtitle="Every receipt the deal's positions produced, in USD at each date's fixing — the Cash Owed page's row for this deal"
    >
      {unavailable ? (
        <div style={{ fontSize: 13, color: theme.textSecondary }}>
          Not in dollars: {unavailable}.
          {Object.entries(cash.generated_native || {}).map(([c, v]) => (
            <div key={c}>Generated: {exactMoney(v, c)}</div>
          ))}
        </div>
      ) : (
        <div className="dr-wf-statement">
          <StatementLine sign="" label="Proceeds generated" value={cents(cash.generated_usd)} strong />
          {(cash.generated_by_kind || []).map((k) => (
            <StatementLine key={k.key} sign="" label={k.label} value={cents(k.usd)} indent />
          ))}
          <StatementLine sign="−" label="Put back into the deal" note="follow-ons and costs paid out of proceeds" value={cents(cash.reinvested_usd)} />
          <StatementLine sign="−" label="Paid to the deal's payees" note="wires, and money moved to their own accounts at Helena Partners Inc" value={cents(cash.paid_usd)} />
          <StatementLine sign="−" label="Recycled into other deals" note="a distribution the moment it goes in" value={cents(cash.recycled_usd)} />
          <StatementLine sign="−" label="Kept by Helena Partners Inc as its own carry" value={cents(cash.kept_usd)} />
          {Number(cash.recovered_usd || 0) !== 0 && (
            <StatementLine sign="+" label="Paid back to the deal" note="over-distributions repaid from another deal" value={cents(cash.recovered_usd)} />
          )}
          <StatementLine
            sign="="
            label="Cash owed now"
            note={cash.owed_since ? `the oldest of it since ${day(cash.owed_since)} (${cash.days_owed} days)` : null}
            value={cents(cash.owed_usd)}
            strong
          />
          <div className="dr-wf-owedto">Who it is due to is in the table above, under Due now.</div>
          <div className="dr-wf-note" style={{ marginTop: 8 }}>
            A distribution reaches this statement once its wire is posted against the deal; one that has left the bank and
            is not yet posted still reads as owed.
            {(cash.not_proceeds || []).length > 0 &&
              ` ${cash.not_proceeds.length} receipt${cash.not_proceeds.length === 1 ? " is" : "s are"} left out as not proceeds: a claim on another Helena entity, or the deal's own distribution arriving at one of Helena's accounts.`}
          </div>
        </div>
      )}
    </Card>
  );
}

/* ── Every movement on the notes ─────────────────────────────────────── */

function HistoryCard({ data, rows }) {
  const order = data.investor_order || [];
  const [showAll, setShowAll] = useState(false);
  const LIMIT = 40;
  const shown = showAll ? rows : rows.slice(-LIMIT);
  const hidden = rows.length - shown.length;
  return (
    <Card
      title="Every movement on the investors' notes"
      subtitle="One row per date and kind, investors across the top; in adds to a note, out takes from it"
      action={<ExportCsvButton filename="deal-investor-history" columns={historyCsvColumns(order)} rows={rows} />}
      noPad
    >
      {hidden > 0 && (
        <div style={{ padding: "8px 20px", fontSize: 12, color: theme.textSecondary }}>
          Showing the latest {LIMIT} of {rows.length}.{" "}
          <button type="button" className="dr-inv-more" onClick={() => setShowAll(true)}>
            Show all
          </button>
        </div>
      )}
      <div className="dr-table-scroll">
        <table className="dr-table dr-hist-table">
          <thead>
            <tr>
              <th className="dr-hist-what">Date and what</th>
              {order.map((name) => (
                <th key={name} className="num">
                  {name}
                </th>
              ))}
              <th className="num">Total</th>
              <th className="num">Notes stand at</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((h) => (
              <tr key={`${h.date}-${h.kind}-${h.other_deal ? h.other_deal.id : ""}`}>
                <td className="dr-hist-what">
                  <div className="dr-wf-note" style={{ marginTop: 0 }}>{day(h.date)}</div>
                  {h.other_deal ? (
                    <>
                      {h.kind === "recycled_in" ? "Recycled in from " : "Recycled into "}
                      <Link className="dr-link" to={dealReportPath(h.other_deal.id)}>
                        {otherDealText(h.other_deal)}
                      </Link>
                    </>
                  ) : (
                    historyLabel(h)
                  )}
                </td>
                {order.map((name) => (
                  <td key={name} className="num" style={h.amounts[name] === undefined ? muted : undefined}>
                    {h.amounts[name] === undefined ? "" : cents(h.sign * Number(h.amounts[name]))}
                  </td>
                ))}
                <td className="num" style={{ fontWeight: 600, color: toneColor(h.signedTotal) }}>
                  {cents(h.signedTotal)}
                </td>
                <td className="num">{cents(h.running)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ── What it rests on ────────────────────────────────────────────────── */

function Footnotes({ data }) {
  const failed = (data.checks || []).filter((c) => !c.ok);
  return (
    <div className="dr-footnote">
      What these figures cannot say
      <InfoTip wide>
        <p>{data.reads_as}</p>
        <ul style={{ margin: "6px 0 0 16px", padding: 0 }}>
          {(data.cannot_say || []).map((text) => (
            <li key={text} style={{ marginBottom: 4 }}>
              {text}
            </li>
          ))}
        </ul>
        {(data.checks || []).map((c) => (
          <p key={c.check}>
            {c.ok ? "✓" : "⚠"} {c.reads_as}
          </p>
        ))}
      </InfoTip>
      {failed.length > 0 && <span style={{ color: theme.danger }}> · {failed.map((c) => c.reads_as).join("; ")}</span>}
    </div>
  );
}

export function InvestorWaterfall({ deal }) {
  const { data, error } = useWaterfall(deal.deal.id);
  const rows = useMemo(() => historyRows(data?.history), [data]);
  if (error) {
    return (
      <Card title="Investors">
        <EmptyState message={error} icon="⚠" />
      </Card>
    );
  }
  if (!data) {
    return (
      <Card title="Investors">
        <LoadingSpinner message="Reading the investors' notes…" />
      </Card>
    );
  }
  const hasInvestors = data.investors.length > 0;
  return (
    <div className="dr-investors">
      {data.known_break && (
        <div className="dr-warning" style={{ marginBottom: theme.space.lg }}>
          <strong style={{ color: theme.text }}>A known break on what this deal owes:</strong> {data.known_break.reason}
          {data.known_break.waiting_on ? ` Waiting on ${data.known_break.waiting_on}.` : ""}
        </div>
      )}
      {hasInvestors ? <Headline data={data} /> : <NoInvestors data={data} />}
      {hasInvestors && <WaterfallCard data={data} />}
      {(hasInvestors || (data.payees || []).length > 0) && <OwedCard data={data} history={rows} />}
      <ProceedsCard data={data} />
      {rows.length > 0 && <HistoryCard data={data} rows={rows} />}
      <Footnotes data={data} />
    </div>
  );
}
